import { client } from '../../../client'
import { useEffect, useState } from 'react'
import {
  Box,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@material-ui/core'
import { DataGrid, GridColDef, GridSortItem } from '@material-ui/data-grid'
import { AdminPage } from '../../../layout/AdminPage'
import { TreatmentProgram } from '../../../types'
import { LoadingIndicator } from '../../../components/LoadingIndicator'

const columns: GridColDef[] = [
  {
    field: 'id',
    headerName: 'ID',
    flex: 0.5,
    renderCell: (params) => (
      <Link
        href="#"
        onClick={(e) => {
          e.preventDefault()
          window.location.href = `/clienten/${params.row.patient?.id}/trajecten/${params.value}`
        }}
        style={{ cursor: 'pointer' }}
      >
        {params.value}
      </Link>
    )
  },
  {
    field: 'patient',
    headerName: 'Patient',
    flex: 1,
    valueGetter: (params) => {
      const patient = params.row.patient
      if (!patient) return ''
      return `${patient.firstName || ''} ${patient.lastName || ''}`.trim()
    }
  },
  {
    field: 'primaryPsychologist',
    headerName: 'Primary Psychologist',
    flex: 1,
    valueGetter: (params) => {
      const psychologist = params.row.primaryPsychologist
      if (!psychologist?.user) return ''
      return `${psychologist.user.firstName || ''} ${
        psychologist.user.lastName || ''
      }`.trim()
    }
  },
  {
    field: 'practice',
    headerName: 'Practice',
    flex: 1,
    valueGetter: (params) => {
      return params.row.practice?.name || ''
    }
  },
  {
    field: 'treatmentType',
    headerName: 'Treatment Type',
    flex: 1,
    valueGetter: (params) => {
      return params.row.treatmentType?.name || ''
    }
  },
  {
    field: 'startDate',
    headerName: 'Start Date',
    flex: 1,
    valueGetter: (params) => {
      return params.row.startDate
        ? new Date(params.row.startDate).toLocaleDateString()
        : ''
    }
  },
  {
    field: 'endDate',
    headerName: 'End Date',
    flex: 1,
    valueGetter: (params) => {
      return params.row.endDate
        ? new Date(params.row.endDate).toLocaleDateString()
        : ''
    }
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    sortable: false,
    valueGetter: (params) => {
      return params.row.funnel?.status?.name || ''
    }
  }
]

const TreatmentProgramListContent = () => {
  const [programs, setPrograms] = useState<TreatmentProgram[]>([])
  const [pageSize, setPageSize] = useState(25)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTreatmentType, setSelectedTreatmentType] = useState<
    number | null
  >(null)
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null)
  const [selectedPractice, setSelectedPractice] = useState<number | null>(null)
  const [selectedPsychologist, setSelectedPsychologist] = useState<
    number | null
  >(null)
  const [treatmentTypes, setTreatmentTypes] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [statuses, setStatuses] = useState<Array<{ id: number; name: string }>>(
    []
  )
  const [practices, setPractices] = useState<
    Array<{ id: number; name: string }>
  >([])
  const [psychologists, setPsychologists] = useState<
    Array<{ id: number; user: { firstName: string; lastName: string } }>
  >([])
  const [sortModel, setSortModel] = useState<GridSortItem[]>([
    { field: 'id', sort: 'desc' }
  ])

  useEffect(() => {
    try {
      client.getTreatmentTypeOptions().then(setTreatmentTypes)
      client.getPracticeOptions(true).then(setPractices)
      client.getPsychologistOptions().then((psychologists) => {
        setPsychologists(
          psychologists.filter((psychologist) => psychologist.user)
        )
      })
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }, [])

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const statuses = await client.getFunnelStatusOptions()
        setStatuses(statuses.filter((status) => !status.isLegacy))
      } catch (error) {
        console.error('Error fetching statuses:', error)
      }
    }
    fetchStatuses()
  }, [])

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true)
        const { programs, total } = await client.getTreatmentPrograms(
          page,
          pageSize,
          sortModel[0]?.field,
          sortModel[0]?.sort === 'desc' ? 'desc' : 'asc',
          selectedTreatmentType,
          selectedStatus,
          selectedPractice,
          selectedPsychologist
        )
        setPrograms(programs)
        setTotal(total)
      } catch (error) {
        console.error('Error fetching treatment programs:', error)
        setError('Failed to fetch treatment programs')
      } finally {
        setLoading(false)
      }
    }
    fetchPrograms()
  }, [
    page,
    pageSize,
    sortModel,
    selectedTreatmentType,
    selectedStatus,
    selectedPractice,
    selectedPsychologist
  ])

  const handleSortModelChange = (model: GridSortItem[]) => {
    setSortModel(model)
    setPage(1) // Reset to first page when sorting changes
  }

  if (loading) {
    return <LoadingIndicator />
  }

  if (error) {
    return (
      <Paper style={{ width: '100%', padding: '20px' }}>
        <Typography color="error">{error}</Typography>
      </Paper>
    )
  }

  return (
    <Paper style={{ width: '100%' }}>
      <Box style={{ width: '100%', height: 'auto', minHeight: 400 }}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px'
          }}
        >
          <Typography variant="h5">Treatment Programs</Typography>
          <Typography variant="h6">
            Page {page} of {Math.ceil(total / pageSize)}
          </Typography>
        </Box>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row-reverse',
            alignItems: 'center',
            gap: '12px',
            padding: '12px'
          }}
        >
          <FormControl variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus || ''}
              onChange={(e) => {
                setSelectedStatus(e.target.value as number | null)
                setPage(1)
              }}
              label="Status"
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              {/* @ts-ignore */}
              <MenuItem value={null}>
                <em>All</em>
              </MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>Treatment Type</InputLabel>
            <Select
              value={selectedTreatmentType || ''}
              onChange={(e) => {
                setSelectedTreatmentType(e.target.value as number | null)
                setPage(1)
              }}
              label="Treatment Type"
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              {/* @ts-ignore */}
              <MenuItem value={null}>
                <em>All</em>
              </MenuItem>
              {treatmentTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>Practice</InputLabel>
            <Select
              value={selectedPractice || ''}
              onChange={(e) => {
                setSelectedPractice(e.target.value as number | null)
                setPage(1)
              }}
              label="Practice"
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              {/* @ts-ignore */}
              <MenuItem value={null}>
                <em>All</em>
              </MenuItem>
              {practices.map((practice) => (
                <MenuItem key={practice.id} value={practice.id}>
                  {practice.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>Psychologist</InputLabel>
            <Select
              value={selectedPsychologist || ''}
              onChange={(e) => {
                setSelectedPsychologist(e.target.value as number | null)
                setPage(1)
              }}
              label="Psychologist"
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                getContentAnchorEl: null
              }}
            >
              {/* @ts-ignore */}
              <MenuItem value={null}>
                <em>All</em>
              </MenuItem>
              {psychologists.map((psychologist) => (
                <MenuItem key={psychologist.id} value={psychologist.id}>
                  {`${psychologist.user.firstName} ${psychologist.user.lastName}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <DataGrid
          columns={columns}
          rows={programs}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => {
            setPageSize(newPageSize)
            setPage(1) // Reset to first page when page size changes
          }}
          onPageChange={(newPage) => setPage(newPage + 1)}
          rowsPerPageOptions={[5, 25, 50, 100]}
          disableSelectionOnClick
          autoHeight
          paginationMode="server"
          rowCount={total}
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          page={page - 1}
          disableColumnMenu
        />
      </Box>
    </Paper>
  )
}

const TreatmentProgramListPage = () => {
  return (
    <AdminPage>
      <TreatmentProgramListContent />
    </AdminPage>
  )
}

export default TreatmentProgramListPage
